import type { AccessDenialReason } from './access';
import type { MaterialFormat, PreviewKind, SizeBucket, UploadRefusal } from './materials';
import type { AcceptRefusal } from './invitations';

/**
 * The event vocabulary. Pure — these functions build payloads and nothing else.
 *
 * Naming lives in the core so the Worker and the browser emit the same strings by
 * construction. A rename that only half-lands is the failure this prevents, and it is
 * invisible until a funnel quietly reads zero — which is exactly what the
 * `objectives_*` → `preview_objectives_*` rename did for two days.
 *
 * Three rules hold every builder below (R19, KTD15):
 *
 * 1. **One name, one side.** Each name is emitted from the browser or from the server,
 *    never both; the emitter is written on each builder. `preview_collab_material_shared`
 *    is browser-side because `growthActivity()` is consulted only inside the browser-side
 *    `track` — a server-emitted copy would never reach the growth metric.
 * 2. **No free text, ever.** Every parameter is an id, a number, a boolean, or a value
 *    from a closed union. No builder takes a filename, title, note, comment body, email
 *    address, or invitation token, and `tests/core/events.test.ts` reads this file to
 *    prove it. Those strings are the product's content; analytics is not where content
 *    goes.
 * 3. **A refusal tells the refused nothing.** `collabAccessDenied` and
 *    `collabInvitationRefused` carry no group or material id at all. The 404 in R5 exists
 *    so a stranger cannot learn that a group is real; an event carrying its id would
 *    answer through the side door what the response refuses to.
 */

export type CollabAction =
  // browser
  | 'page_viewed'
  | 'group_opened'
  | 'material_shared'
  | 'material_previewed'
  | 'kill_switch_shown'
  | 'labs_clicked'
  | 'standards_tab_opened'
  // server
  | 'group_created'
  | 'group_renamed'
  | 'member_removed'
  | 'member_left'
  | 'member_promoted'
  | 'member_demoted'
  | 'invitation_created'
  | 'invitation_revoked'
  | 'invitation_accepted'
  | 'invitation_declined'
  | 'invitation_refused'
  | 'material_refused'
  | 'planner_upload'
  | 'material_downloaded'
  | 'material_deleted'
  | 'material_restored'
  | 'material_purged'
  | 'material_renamed'
  | 'comment_posted'
  | 'comment_deleted'
  | 'access_denied'
  | 'standards_check'
  | 'standards_context_saved'
  | 'thread_read';

export type EventName = `preview_collab_${CollabAction}`;

export type EventPropertyValue = string | number | boolean;

export interface AnalyticsEvent {
  name: EventName;
  properties: Record<string, EventPropertyValue>;
}

/**
 * The one place a name is assembled. Handlers call the named builders below rather than
 * this, so that rule 2 is checkable by reading one file.
 */
export function previewCollabAction(
  action: CollabAction,
  properties: Record<string, EventPropertyValue> = {},
): AnalyticsEvent {
  return { name: `preview_collab_${action}`, properties };
}

// --- browser ------------------------------------------------------------------------------

/**
 * Where an arrival came from, when it came from another 240 Labs tool. Only the Planner's
 * hand-off sets it (KTD7); the URL that carried it is stripped before analytics starts, so
 * the property is the only record of it.
 */
export type ArrivalSource = 'planner';
/** The one arrival source: a Planner hand-off's `from`, in its fragment, request body, and events. */
export const ARRIVAL_SOURCE: ArrivalSource = 'planner';

/** The arrival. `from` is set on the first arrival after a Planner hand-off, and only then. */
export const collabPageViewed = (from?: ArrivalSource): AnalyticsEvent =>
  previewCollabAction('page_viewed', from ? { from } : {});

export const collabGroupOpened = (groupId: string, memberCount: number, materialCount: number): AnalyticsEvent =>
  previewCollabAction('group_opened', { group_id: groupId, member_count: memberCount, material_count: materialCount });

/** Collab's qualifying "meaningful action" for the non-subscriber growth metric (KTD15). */
export const collabMaterialShared = (
  groupId: string,
  materialId: string,
  bucket: SizeBucket,
  format: MaterialFormat,
): AnalyticsEvent =>
  previewCollabAction('material_shared', {
    group_id: groupId,
    material_id: materialId,
    size_bucket: bucket,
    material_format: format,
  });

/**
 * A material shown inline rather than downloaded (U11).
 *
 * Browser-side, and browser-side is the only side it could be: the Worker serves the bytes
 * but cannot tell a preview that rendered from one that was fetched and thrown away by a
 * closing tab, and `preview_collab_material_downloaded` is already the server's record that
 * bytes left. Two names, one side each (KTD15).
 *
 * `preview_kind` is the closed union the viewer actually branched on — `pdf` or `image` —
 * and not the content type, so the payload cannot become a free-text field later.
 */
export const collabMaterialPreviewed = (
  groupId: string,
  materialId: string,
  kind: PreviewKind,
): AnalyticsEvent =>
  previewCollabAction('material_previewed', {
    group_id: groupId,
    material_id: materialId,
    preview_kind: kind,
  });

export const collabKillSwitchShown = (): AnalyticsEvent => previewCollabAction('kill_switch_shown');

/** The way back to 240 Labs from the bar: the mark, or the Labs chip. */
export type LabsPlacement = 'logo' | 'navigation';

export const collabLabsClicked = (placement: LabsPlacement): AnalyticsEvent =>
  previewCollabAction('labs_clicked', { placement });

export const collabStandardsTabOpened = (): AnalyticsEvent => previewCollabAction('standards_tab_opened');

// --- server -------------------------------------------------------------------------------

/**
 * Server-only lifecycle, no material content, context or provider error strings. `from` is
 * set on the first check after a Planner hand-off, which the browser says in the request body.
 */
export const collabStandardsCheck = (status: 'started' | 'completed' | 'failed', from?: ArrivalSource): AnalyticsEvent =>
  previewCollabAction('standards_check', { status, event_source: 'server', ...(from ? { from } : {}) });

/** Where a standards context was saved: the group default, or one material's override. */
export type StandardsContextScope = 'group' | 'material';

/**
 * A group default saved, an override saved, or an override cleared (R11, KTD6). `cleared` is
 * true when the save stored nothing, and `fields` counts the fields it stored (0-3). The
 * values themselves are never sent, matching `standards_check`.
 */
export const collabStandardsContextSaved = (
  groupId: string,
  scope: StandardsContextScope,
  cleared: boolean,
  fields: number,
): AnalyticsEvent =>
  previewCollabAction('standards_context_saved', { group_id: groupId, scope, cleared, fields });

export const collabGroupCreated = (groupId: string, groupCount: number): AnalyticsEvent =>
  previewCollabAction('group_created', { group_id: groupId, group_count: groupCount });

/** The new name is the group's content and stays out of the payload; the id says which. */
export const collabGroupRenamed = (groupId: string): AnalyticsEvent =>
  previewCollabAction('group_renamed', { group_id: groupId });

export const collabMemberRemoved = (groupId: string, memberCount: number): AnalyticsEvent =>
  previewCollabAction('member_removed', { group_id: groupId, member_count: memberCount });

export const collabMemberLeft = (groupId: string, memberCount: number): AnalyticsEvent =>
  previewCollabAction('member_left', { group_id: groupId, member_count: memberCount });

export const collabMemberPromoted = (groupId: string, adminCount: number): AnalyticsEvent =>
  previewCollabAction('member_promoted', { group_id: groupId, admin_count: adminCount });

export const collabMemberDemoted = (groupId: string, adminCount: number): AnalyticsEvent =>
  previewCollabAction('member_demoted', { group_id: groupId, admin_count: adminCount });

/**
 * What happened to the invitation's email (KTD11). `not_configured` is no SendGrid key,
 * `disabled` is the kill switch. A link is returned in every case, so every value except
 * `sent` means the admin still has a link to pass on by hand. `not_internal` was retired when
 * early access ended on 2026-10-13; events before then still carry it.
 */
export type InvitationEmailResult = 'sent' | 'failed' | 'not_configured' | 'disabled';

/**
 * `resend` is a property rather than a name of its own: a resend mints a new invitation the
 * same way, and a reader counting new invitations filters it out instead of joining two names.
 */
export const collabInvitationCreated = (
  groupId: string,
  emailResult: InvitationEmailResult,
  resend: boolean,
): AnalyticsEvent =>
  previewCollabAction('invitation_created', { group_id: groupId, email_result: emailResult, resend });

export const collabInvitationRevoked = (groupId: string): AnalyticsEvent =>
  previewCollabAction('invitation_revoked', { group_id: groupId });

/** Which way in: the link from the email or the copied link, or the groups-page banner. */
export type AcceptPath = 'link' | 'banner';

export const collabInvitationAccepted = (groupId: string, memberCount: number, via: AcceptPath): AnalyticsEvent =>
  previewCollabAction('invitation_accepted', { group_id: groupId, member_count: memberCount, via });

/** Which way out: the decline link in the email, or the groups-page banner. */
export type DeclinePath = 'email' | 'banner';

/** The invitee said no (R11). Carries the group, because the decliner was its addressee. */
export const collabInvitationDeclined = (groupId: string, via: DeclinePath): AnalyticsEvent =>
  previewCollabAction('invitation_declined', { group_id: groupId, via });

/**
 * Fired for someone who is, by definition, not in the group — so it carries the reason and
 * nothing else. The client sees one indistinguishable `invitation_unusable` (R10); this is
 * the server's private record of which branch actually fired.
 */
export const collabInvitationRefused = (reason: AcceptRefusal): AnalyticsEvent =>
  previewCollabAction('invitation_refused', { reason });

/**
 * A material uploaded through the Planner into the caller's "My Planner" group (planner
 * collab materials KTD8, R12). Server-only, once per stored material, never on a refusal —
 * a refusal is `material_refused`, as on the group route. Separate from the browser-only
 * `material_shared`: the browser that made this upload is the Planner's, not Collab's.
 */
export const collabPlannerUpload = (
  groupId: string,
  materialId: string,
  bucket: SizeBucket,
  format: MaterialFormat,
): AnalyticsEvent =>
  previewCollabAction('planner_upload', {
    group_id: groupId,
    material_id: materialId,
    size_bucket: bucket,
    format,
  });

export const collabMaterialRefused = (groupId: string, reason: UploadRefusal, bucket: SizeBucket): AnalyticsEvent =>
  previewCollabAction('material_refused', { group_id: groupId, reason, size_bucket: bucket });

export const collabMaterialDownloaded = (groupId: string, materialId: string): AnalyticsEvent =>
  previewCollabAction('material_downloaded', { group_id: groupId, material_id: materialId });

export const collabMaterialDeleted = (groupId: string, materialId: string, byAdmin: boolean): AnalyticsEvent =>
  previewCollabAction('material_deleted', { group_id: groupId, material_id: materialId, by_admin: byAdmin });

/**
 * The trash. `material_deleted` still fires on delete and still means "taken out of the
 * group" — it no longer means the bytes are gone. These two say what happened next: brought
 * back, or deleted for good by a teacher. The sweeper's thirty-day purge is not a teacher's
 * action and emits nothing.
 */
export const collabMaterialRestored = (groupId: string, materialId: string, byAdmin: boolean): AnalyticsEvent =>
  previewCollabAction('material_restored', { group_id: groupId, material_id: materialId, by_admin: byAdmin });

export const collabMaterialPurged = (groupId: string, materialId: string, byAdmin: boolean): AnalyticsEvent =>
  previewCollabAction('material_purged', { group_id: groupId, material_id: materialId, by_admin: byAdmin });

/** A new title, never the title. `by_admin` as on delete: an admin retitled another's. */
export const collabMaterialRenamed = (groupId: string, materialId: string, byAdmin: boolean): AnalyticsEvent =>
  previewCollabAction('material_renamed', { group_id: groupId, material_id: materialId, by_admin: byAdmin });

/**
 * `is_anchored` is a property of an existing event rather than an event of its own, and that
 * is the whole reason it is a boolean here instead of `preview_collab_comment_anchored` over
 * in the browser.
 *
 * Attaching a comment to a passage is not a second action a teacher takes — it is a fact about
 * the comment they just posted — and a second name would be a second event for one action, on
 * the other side of the wire, that every reader of `comment_posted` would have to join back to
 * it. Adding a property breaks no reader; a new name would need one.
 */
export const collabCommentPosted = (
  groupId: string,
  materialId: string,
  isReply: boolean,
  isAnchored: boolean,
): AnalyticsEvent =>
  previewCollabAction('comment_posted', {
    group_id: groupId,
    material_id: materialId,
    is_reply: isReply,
    is_anchored: isAnchored,
  });

export const collabCommentDeleted = (groupId: string, materialId: string, byAdmin: boolean): AnalyticsEvent =>
  previewCollabAction('comment_deleted', { group_id: groupId, material_id: materialId, by_admin: byAdmin });

/**
 * A mark-read that cleared at least one unread comment (R12, KTD9). Server-side, because only
 * the Worker knows how many comments the watermark moved past. A mark-read that cleared
 * nothing fires nothing, so the count of these is the count of threads read, not of opens.
 */
export const collabThreadRead = (groupId: string, materialId: string, cleared: number): AnalyticsEvent =>
  previewCollabAction('thread_read', { group_id: groupId, material_id: materialId, cleared });

/**
 * What a refusal may say. A closed union, so no free text can reach the payload.
 *
 * The door's own reasons come from `core/access.ts` rather than being restated here: an
 * operator reading this event needs `no_email` and `disabled` kept apart, and a second copy
 * of that union is how one of them silently stops being reported.
 */
export type DenialReason =
  | 'not_a_member' | 'not_permitted' | 'rate_limited' | AccessDenialReason;

/**
 * No group id, no material id, no address — for any reason, not only `not_a_member`.
 * Distinguishing the two cases at the payload level would reintroduce the leak one
 * mis-edit later; carrying nothing costs one join in PostHog and cannot regress.
 */
export const collabAccessDenied = (reason: DenialReason): AnalyticsEvent =>
  previewCollabAction('access_denied', { reason });
